import * as React from 'react'
import { isApiErrorCode } from '@lib/constants/api-error-codes'
import type { ConsentApproveInput } from '@lib/input/mcp.input'
import type { Uuid } from '@lib/validators/params'
import { isApiError } from '@/helpers/apiClient'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import type { IMcpConsentService } from '@/types/McpConsentService.types'

/** Messages for the consent form: on the code field, on the acknowledgement, or for the whole form. */
export interface ConsentErrors {
  code?: string
  acknowledged?: string
  form?: string
}

/** Field-level messages for the code and acknowledgement; anything else is a form error. */
function errorsFrom<E>(failure: E): ConsentErrors {
  if (isApiError(failure)) {
    const message = failure.body.message ?? 'Something went wrong.'
    const code = failure.body.code
    if (code !== undefined && isApiErrorCode(code)) {
      if (code === 'mcp.grant_code_invalid' || code === 'mcp.grant_code_required') return { code: message }
      if (code === 'mcp.acknowledgement_required') return { acknowledged: message }
    }
  }
  return { form: apiErrorMessage(failure, 'Could not connect the app.') }
}

/**
 * Approving or denying a connection request. Both end by handing the browser
 * back to the AI app (`onDone`), so the buttons stay disabled once one succeeds.
 */
export function useConsentSubmit(requestId: Uuid, service: IMcpConsentService, onDone: (redirectUrl: string) => void) {
  const [pending, setPending] = React.useState<'approve' | 'deny' | null>(null)
  const [errors, setErrors] = React.useState<ConsentErrors>({})

  async function approve(input: ConsentApproveInput): Promise<void> {
    setErrors({})
    setPending('approve')
    try {
      onDone((await service.approve(requestId, input)).redirectUrl)
    } catch (failure) {
      setPending(null)
      setErrors(errorsFrom(failure))
    }
  }

  async function deny(): Promise<void> {
    setErrors({})
    setPending('deny')
    try {
      onDone((await service.deny(requestId)).redirectUrl)
    } catch (failure) {
      setPending(null)
      setErrors({ form: apiErrorMessage(failure, 'Could not decline the request.') })
    }
  }

  return { pending, errors, setErrors, approve, deny }
}
